import Dexie from 'dexie';
import { useLiveQuery } from "dexie-react-hooks";
import { fetchItems } from "../API/items/itemsAPI";
import {convertIndexedItem, decryptItemWithMeta} from "../Item/utils";
import {estimateItemTokens} from "../Chat/utils";
import {createEmbedding} from "../API/OpenAIAPI";


export const useQuery = useLiveQuery;

function BetweenAddon(db) {
    const originalBetween = db.WhereClause.prototype.between
    db.WhereClause.prototype.between = function (a, b, includeLower = true, includeUpper = true) {
        return originalBetween.call(this, a, b, includeLower, includeUpper)
    }
}

// Utility function to calculate Euclidean distance
function euclideanDistance(vec1, vec2) {
    return Math.sqrt(vec1.reduce((sum, val, i) => sum + Math.pow(val - vec2[i], 2), 0));
}

function SemanticSearchAddon(db) {
    db.Collection.prototype.semanticSearch = function (query, {limit = 10}) {
        const collection = this;
        return {
            async toArray() {
                const items = await collection.toArray();
                if (items.length === 0) return [];
                const response = await createEmbedding(query, items[0]?.embeddingModel, db.KBData);
                const queryVector = response.data[0]?.embedding.slice(0, (items[0]?.embeddings?.length - 1))
                const distances = items.map(item => ({item, distance: euclideanDistance(queryVector, item.embeddings)}));
                distances.sort((a, b) => a.distance - b.distance);
                return distances.slice(0, limit).map(d => d.item);
            }
        }
    }
}

function KNNAddon(db) {
    db.knn = async function ({inputVector, maxItems, maxTokens}) {
        const allItems = [];

        if (maxItems && Number.isNaN(parseInt(maxItems))) maxItems = 100;

        // Iterate over all tables and collect items
        for (const table of db.tables) {
            const items = await table.toArray();
            items.forEach(item => {
                item.itemType = table.name; // Add the table name as itemType
            });
            allItems.push(...items);
        }

        if (allItems.length === 0) return [];

        const distances = allItems.map(item => ({
            item,
            distance: euclideanDistance(inputVector, item.embeddings)
        }));

        distances.sort((a, b) => a.distance - b.distance);

        const res = [];
        let totalTokens = 0;

        for (const d of distances) {
            if (maxItems && res.length >= maxItems) break;
            const itemData = convertIndexedItem({ ...d.item });
            const estimatedTokens = estimateItemTokens(itemData);
            if (maxTokens && (totalTokens + estimatedTokens) > maxTokens) break;
            totalTokens += estimatedTokens;
            const score = d.distance === 0 ? 1 : 1 / d.distance;
            const item = {...itemData, meta: {...itemData.meta, itemType: d.item.itemType, score, estimatedTokens}};
            const decryptedItem = decryptItemWithMeta(item)
            res.push(decryptedItem);
        }

        return res;
    };
}

function HasItemsAddon(db) {
    db.hasItems = async function () {
        for (const table of db.tables) {
            const count = await table.count();
            if (count > 0) return true;
        }
        return false;
    };
}

function ToItemsAddon(db) {
    db.Table.prototype.toItems = function () {
        return new Promise((resolve) => {
            this.toArray().then(items => {
                resolve(items.map(item => decryptItemWithMeta(convertIndexedItem(item, this.name))))
            })
        })
    }

    db.Collection.prototype.toItems = function () {
        return new Promise((resolve) => {
            this.toArray().then(items => {
                resolve(items.map(item => decryptItemWithMeta(convertIndexedItem(item, this._ctx.table.name))))
            })
        })
    }
}

// Register the addon
Dexie.addons.push(SemanticSearchAddon);
Dexie.addons.push(BetweenAddon);
Dexie.addons.push(KNNAddon);
Dexie.addons.push(HasItemsAddon);
Dexie.addons.push(ToItemsAddon);


export let db;

const putMetadata = async (data) => {
    localStorage.setItem('IndexedDB', JSON.stringify(data));
}

const getMetadata = () => {
    const data = localStorage.getItem('IndexedDB')
    if (data) {
        return JSON.parse(data)
    } else {
        const initState = { lastUpdatedAt: 1, verno: 1, currentSchema: {} }
        putMetadata(initState)
        return initState
    }
}

async function changeSchema(schemaChanges) {
    db.close();

    const newDb = new Dexie(db.name);

    newDb.on('blocked', () => false); // Silence console warning of blocked event.

    // If DB is empty from tables, it needs to be recreated
    if (db.tables.length === 0) {
        await db.delete();
        newDb.version(1).stores(schemaChanges);
        putMetadata({...getMetadata(), verno: 1, currentSchema: schemaChanges})
        await newDb.open();
        db = newDb
        return db
    }

    // Extract current schema in dexie format:
    const currentSchema = db.tables.reduce((result, { name, schema }) => {
        result[name] = [ schema.primKey.src, ...schema.indexes.map((idx) => idx.src) ].join(',');
        return result;
    }, {});

    // Tell Dexie about current schema:
    newDb.version(db.verno).stores(currentSchema);
    // Tell Dexie about next schema:
    newDb.version(db.verno + 1).stores(schemaChanges);


    putMetadata({...getMetadata(), verno: (db.verno + 1), currentSchema: {
        ...currentSchema,
        ...schemaChanges
    }})

    // Upgrade it:
    await newDb.open();
    db = newDb
    return db
}

const indexItems = async ({items, metadata}) => {
    // // Create Tables
    const newSchema = {};
    items.forEach(({ item, meta }) => {
        const { itemType, attributes, embeddingDimension } = meta;

        let expectedSchema = 'itemId, ' + attributes.map(a => a.attrName)?.join(', ');
        if (embeddingDimension) expectedSchema += `, embeddingModel, totalTokens`

        expectedSchema += `, updatedAt, createdAt`

        // create new schema type or update existing
        if (!metadata.currentSchema[itemType] || metadata.currentSchema[itemType] !== expectedSchema) {
            newSchema[itemType] = expectedSchema
        }
    });

    if (Object.keys(newSchema).length > 0) {
        await changeSchema(newSchema)
    }

    // // Insert Records
    let latestUpdate = 0;
    const records = {};
    let i = 0;
    for (const { item, meta } of items) {
        const { itemType, embeddingDimension, embeddingModel, itemId, updatedAt, createdAt, totalTokens, attributes } = meta;
        const embeddings = embeddingDimension ? meta[`embeddings${embeddingDimension}`] : null;
        if (!records[itemType]) records[itemType] = [];
        records[itemType].push({itemId, updatedAt, createdAt, ...item, embeddings, embeddingModel, totalTokens, attributes});
        if (updatedAt > latestUpdate) latestUpdate = updatedAt;
        i++;
    }

    if (Object.keys(records)?.length) {
        try {
            for (const itemType of Object.keys(records)) {
                if (records?.[itemType]?.length) await db[itemType].bulkPut(records[itemType])
            }
            await putMetadata({...getMetadata(), lastUpdatedAt: latestUpdate})
        } catch (e) {
            console.log('Failed to update Dexie', e.toString())
        }
    }
}

export const initDB = async ({ KBData, setSystemAlert, setLoading }) => {
    const dbName = 'IndexedDB_' + KBData.kbId;
    db = new Dexie(dbName);

    const metadata = await getMetadata();

    if (Object.keys(metadata?.currentSchema).length) {
        db.version(metadata.verno).stores(metadata.currentSchema);
    }

    if (setLoading) setLoading(true);
    let { items, lastEvaluatedKey } = await fetchItems(KBData, { field: 'updatedAt', from: (metadata.lastUpdatedAt + 1) });

    let i = items?.length


    if (i > 0 && setSystemAlert) setSystemAlert({msg: `Indexing items ...`, type: 'info', duration: 60000})

    await indexItems({ items, metadata, setSystemAlert });

    while (lastEvaluatedKey?.updatedAt) {
        const response = await fetchItems(KBData, {
            exclusiveStartKey: lastEvaluatedKey,
            field: 'updatedAt'
        });
        items = response.items;
        if (items?.length > 0 && setSystemAlert) setSystemAlert({msg: `${items?.length} item${i === 1 ? '' : 's'} indexed ...`, type: 'info', duration: 3000})

        i += items?.length
        lastEvaluatedKey = response.lastEvaluatedKey;
        await indexItems({ items, metadata, setSystemAlert });
    }

    if (i > 0 && setSystemAlert) setSystemAlert({msg: `Total ${i} item${i === 1 ? '' : 's'} indexed`, type: 'info', duration: 10000})

    if (setLoading) setLoading(prev => prev === 0 ? (prev - 1) : false);

    return db;
};

async function getIndexedDBStores(dbName) {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const stores = {};

            const storeNames = Array.from(db.objectStoreNames);
            let pendingStores = storeNames.length;

            storeNames.forEach((storeName) => {
                const transaction = db.transaction(storeName, 'readonly');
                const store = transaction.objectStore(storeName);
                const getAllRequest = store.getAll();

                getAllRequest.onsuccess = () => {
                    stores[storeName] = getAllRequest.result;
                    pendingStores -= 1;
                    if (pendingStores === 0) {
                        resolve(stores);
                    }
                };

                getAllRequest.onerror = (event) => {
                    reject(`Failed to get all values from store ${storeName}: ${event.target.error}`);
                };
            });

            if (storeNames.length === 0) {
                resolve(stores);
            }
        };

        request.onerror = (event) => {
            reject(`Failed to open database ${dbName}: ${event.target.error}`);
        };
    });
}
